import { ChangeDetectionStrategy, ChangeDetectorRef, Component, CUSTOM_ELEMENTS_SCHEMA, OnInit, ViewChild } from '@angular/core';
import { interval, timer, Subject } from 'rxjs';
import { AuthService } from './core/auth/auth.service';
import { ApplicationsService } from './modules/applications/applications.service';
import { filter, takeUntil } from 'rxjs/operators';
import { MatDrawer, MatSidenavModule } from '@angular/material/sidenav';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import { AlertService } from './shared/services/alert.service';
import { SW_WORKER_MESSAGE } from './constants/sw-worker-message.constants';
import { MatIconModule } from '@angular/material/icon';
import { waitForDOMReady } from './shared/functions/utils.type';
import { DrawerFormService } from './shared/services/drawer-form.service';
import { DataFormEvent, DataFormRequest } from './shared/components/data-form/data-form.model';
import { DataFormModule } from './shared/components/data-form/data-form.module';
import { FormlyFieldConfig } from '@ngx-formly/core';
@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./app.component.scss'],
    standalone: true,
    imports: [
        RouterOutlet, MatSidenavModule, MatIconModule,
        DataFormModule,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppComponent implements OnInit {
    @ViewChild('innerDrawerInfoWindow') innerDrawerInfoWindow: MatDrawer;
    drawerMode: 'over' | 'side' = 'side';
    drawerOpened: boolean = false;
    drawerAction: 'help' | 'data-form' | null = null;
    title: any;
    text: any;

    // Data form drawer state
    innerModel: any;
    innerForm: string;
    innerFormFields: FormlyFieldConfig[];
    innerEndpoint: string;
    idField: string;
    skipApi: boolean = false;
    innerDictionary: any;
    isFixed: boolean = false;

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(
        private readonly authService: AuthService
        , private readonly router: Router
        , private readonly appService: ApplicationsService
        , private readonly changeDetectorRef: ChangeDetectorRef
        , private readonly alertService: AlertService
        , private readonly swUpdate: SwUpdate
        , private readonly drawerFormService: DrawerFormService
    ) {

        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            takeUntil(this._unsubscribeAll)
        ).subscribe((res) => {
            this.closeSideDrawer();
        });

        //repeat every 20 minutes
        timer(0, 20 * 60 * 1000).subscribe(() => {
            this.authService.check().subscribe((isLogged) => {
                if (!isLogged) return;
                this.authService.signInUsingToken().subscribe(() => {
                });
            });
        });

        this.appService.openHelpSideDrawer$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((res: any) => {
                if (res) {
                    this.title = res?.title;
                    this.text = res?.text;
                    this.drawerAction = 'help';
                    this.drawerMode = 'side';
                    this.drawerOpened = true;
                }
                this.changeDetectorRef.markForCheck();
            });

        this.drawerFormService.openFormRequest$
            .pipe(
                takeUntil(this._unsubscribeAll),
                filter(request => request !== null)
            )
            .subscribe((request: DataFormRequest) => {
                this.renderDrawerForm(request);
            });
    }

    ngOnInit(): void {
        if (this.swUpdate.isEnabled) {
            this.swUpdate.versionUpdates.subscribe((event) => {
                if (event.type === SW_WORKER_MESSAGE.VERSION_READY) {
                    console.info('A new version is available!');
                    this.alertService.confirm('A new version of the application is available. Would you like to update?').then((response) => {
                        if (response.isConfirmed) {
                            globalThis.location.reload();
                        }
                    });
                }
            });

            // Check for updates every 30 minutes
            interval(30 * 60 * 1000)
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(() => {
                    this.swUpdate.checkForUpdate();
                });
        }
    }

    renderDrawerForm(request: DataFormRequest): void {
        this.isFixed = false;
        if (request.formUrl === 'change-standard-for-sales-quote' || request.formUrl === 'generic-dropdown') {
            this.isFixed = true;
        }
        this.innerModel = null;
        this.drawerMode = 'over';
        this.drawerOpened = true;
        this.innerDrawerInfoWindow?.open();
        setTimeout(() => {
            this.innerModel = request.model;
            if (request.formUrl) {
                this.innerFormFields = null;
                this.innerForm = request.formUrl;
            } else if (request.fields) {
                this.innerForm = null;
                this.innerFormFields = request.fields;
            }
            this.drawerAction = 'data-form';
            this.innerEndpoint = request.apiUrl;
            this.idField = request.idFieldName;
            this.innerDictionary = request.dictionary;
            this.skipApi = (request.skipApi === true);
            this.changeDetectorRef.markForCheck();
        }, 100);
    }

    onDrawerFormSubmit(event: DataFormEvent): void {
        this.drawerFormService.emitSubmit(event);
        this.closeDrawerForm();
    }

    onDrawerFormCancel(): void {
        this.drawerFormService.emitCancel();
        this.closeDrawerForm();
    }

    onDrawerClosed(): void {
        if (this.drawerAction === 'data-form') {
            this.drawerFormService.emitCancel();
        }
        this.innerModel = null;
        this.drawerAction = null;
        this.drawerOpened = false;
        this.appService.closedHelpSideDrawer.next(true);
        this.changeDetectorRef.markForCheck();
    }

    private closeDrawerForm(): void {
        this.innerModel = null;
        this.drawerAction = null;
        this.drawerOpened = false;
        this.innerDrawerInfoWindow?.close();
        this.changeDetectorRef.markForCheck();
    }

    closeSideDrawer(): void {
        waitForDOMReady().then(() => {
            this.changeDetectorRef.markForCheck();
        });
        if (this.drawerAction === 'data-form') {
            this.drawerFormService.emitCancel();
        }
        this.drawerOpened = false;
        this.drawerAction = null;
        this.appService.closedHelpSideDrawer.next(true);
        this.innerDrawerInfoWindow?.close();
    }
}
